import { useEffect, useState } from 'react';
import { formatCurrency } from '../../lib/format';
import { X, Printer, Package } from 'lucide-react';
import { MarkupCatalog, Product } from '@bloom/shared';
import { apiClient } from '../../lib/api';
import { useTenantBranding } from '../../hooks/useTenantBranding-runtime';
import { formatDate } from '../../utils/dateUtils';
import { computeVialMath, formatTotalMgBare } from '../../utils/vialMath';
import { PHARMACIES } from '../../lib/pharmacyRouting';

interface CatalogPrintViewProps {
  catalog: MarkupCatalog;
  onClose: () => void;
}

interface ProductWithDetails extends Product {
  catalogPrice: number;
  notes?: string;
}

export function CatalogPrintView({ catalog, onClose }: CatalogPrintViewProps) {
  const { branding, colors } = useTenantBranding();
  const [products, setProducts] = useState<ProductWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const currentYear = new Date().getFullYear();

  // Add body class for print styling
  useEffect(() => {
    document.body.classList.add('printing-catalog');
    return () => {
      document.body.classList.remove('printing-catalog');
    };
  }, []);

  useEffect(() => {
    const fetchProductDetails = async () => {
      const catalogProducts = catalog.products || [];
      if (catalogProducts.length === 0) {
        setLoading(false);
        return;
      }

      try {
        const productIds = catalogProducts.map((p: any) => p.productId);
        const response = await apiClient.getProductsByIds(productIds);

        if (response.success && response.data) {
          // Map product details with catalog prices
          const productsWithPrices = response.data.products.map((product: Product) => {
            const catalogProduct = catalogProducts.find((p: any) => p.productId === product._id);
            return {
              ...product,
              catalogPrice: catalogProduct?.price || product.basePrice,
              notes: catalogProduct?.notes
            };
          });

          // Sort by drug name
          productsWithPrices.sort((a: ProductWithDetails, b: ProductWithDetails) =>
            a.drugName.localeCompare(b.drugName)
          );

          setProducts(productsWithPrices);
        }
      } catch (error) {
        console.error('Failed to fetch product details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProductDetails();
  }, [catalog]);

  const handlePrint = () => {
    window.print();
  };


  return (
    <div id="catalog-print-view" className="fixed inset-0 z-50 overflow-y-auto print:static print:overflow-visible">
      {/* Backdrop - hidden when printing */}
      <div className="fixed inset-0 bg-black bg-opacity-50 print:hidden" onClick={onClose} />

      {/* Modal */}
      <div className="relative min-h-screen flex items-start justify-center p-4 print:p-0 print:min-h-0 print:block">
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl my-8 print:my-0 print:shadow-none print:rounded-none print:max-w-none print:w-full">
          {/* Header - hidden when printing */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 print:hidden">
            <h2 className="text-lg font-semibold text-gray-900">Print Catalog</h2>
            <div className="flex items-center gap-3">
              <button
                onClick={handlePrint}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print
              </button>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Print Content */}
          <div className="print:p-0">
            {/* Header with Logo and Brand Color */}
            <div
              className="px-8 py-6 print:px-6 print:py-4"
              style={{ backgroundColor: colors.primary }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {branding.logo && (
                    <img
                      src={branding.logo}
                      alt={branding.companyName}
                      className="h-10 w-auto print:h-8 brightness-0 invert"
                    />
                  )}
                  <div className="text-white">
                    <div className="text-lg font-semibold print:text-base">{branding.companyName}</div>
                    <div className="text-sm opacity-90 print:text-xs">Product Catalog</div>
                  </div>
                </div>
                <div className="text-right text-white text-sm print:text-xs">
                  <div className="opacity-90">{formatDate(new Date(), { month: 'long' })}</div>
                </div>
              </div>
            </div>

            {/* Catalog Title Section */}
            <div className="px-8 py-6 print:px-6 print:py-4 border-b border-gray-200">
              <h1 className="text-2xl font-bold text-gray-900 print:text-xl">
                {catalog.catalogName}
              </h1>
              {catalog.description && (
                <p className="text-gray-600 mt-2 print:text-sm">{catalog.description}</p>
              )}

              {/* Catalog Info */}
              <div className="flex flex-wrap gap-4 mt-4 text-sm text-gray-600 print:text-xs print:gap-3">
                <div className="flex items-center gap-1">
                  <Package className="w-4 h-4 text-gray-400" />
                  <span className="font-medium">{products.length}</span> Products
                </div>
                {catalog.tags && catalog.tags.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400">|</span>
                    <div className="flex gap-1">
                      {catalog.tags.map((tag: string, index: number) => (
                        <span
                          key={index}
                          className="px-2 py-0.5 rounded text-xs"
                          style={{ backgroundColor: `${colors.primary}15`, color: colors.primary }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Products Section */}
            <div className="px-8 py-6 print:px-6 print:py-4">

            {/* Loading State */}
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Loading products...</p>
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No products in this catalog</p>
              </div>
            ) : (
              /* Products Table */
              <table className="w-full border-collapse">
                <thead>
                  <tr
                    style={{ backgroundColor: `${colors.primary}10` }}
                  >
                    <th
                      className="text-left py-3 px-3 text-sm font-semibold print:text-xs print:py-2"
                      style={{ color: colors.darker }}
                    >
                      Product
                    </th>
                    <th
                      className="text-left py-3 px-3 text-sm font-semibold print:text-xs print:py-2"
                      style={{ color: colors.darker }}
                    >
                      Format
                    </th>
                    <th
                      className="text-left py-3 px-3 text-sm font-semibold print:text-xs print:py-2"
                      style={{ color: colors.darker }}
                    >
                      Size
                    </th>
                    <th
                      className="text-left py-3 px-3 text-sm font-semibold print:text-xs print:py-2"
                      style={{ color: colors.darker }}
                    >
                      Total mg
                    </th>
                    <th
                      className="text-left py-3 px-3 text-sm font-semibold print:text-xs print:py-2"
                      style={{ color: colors.darker }}
                    >
                      Fulfilled by
                    </th>
                    <th
                      className="text-right py-3 px-3 text-sm font-semibold print:text-xs print:py-2"
                      style={{ color: colors.darker }}
                    >
                      Price
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product, index) => (
                    <tr
                      key={product._id}
                      className={`border-b border-gray-100 ${index % 2 === 1 ? 'bg-gray-50' : ''}`}
                    >
                      <td className="py-3 px-3 print:py-2">
                        <div className="text-sm font-medium text-gray-900 print:text-xs">
                          {product.drugName}
                        </div>
                        {product.concentration && (
                          <div className="text-xs text-gray-500">
                            {product.concentration}
                          </div>
                        )}
                        {product.notes && (
                          <div
                            className="text-xs mt-1"
                            style={{ color: colors.primary }}
                          >
                            {product.notes}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-3 text-sm text-gray-600 print:text-xs print:py-2">
                        {product.format}
                      </td>
                      <td className="py-3 px-3 text-sm text-gray-600 print:text-xs print:py-2">
                        {product.size}
                      </td>
                      <td className="py-3 px-3 text-sm text-gray-600 print:text-xs print:py-2">
                        {formatTotalMgBare(computeVialMath(product)) || '—'}
                      </td>
                      <td className="py-3 px-3 text-sm text-gray-600 print:text-xs print:py-2">
                        {product.isPeptide ? PHARMACIES.bluefive.name : PHARMACIES.greenstone.name}
                      </td>
                      <td className="py-3 px-3 text-right print:py-2">
                        <span className="text-sm font-semibold text-gray-900 print:text-xs">
                          {formatCurrency(product.catalogPrice)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            </div>

            {/* Footer */}
            <div
              className="px-8 py-4 print:px-6 print:py-3 border-t border-gray-200"
              style={{ backgroundColor: '#f9fafb' }}
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs text-gray-500">
                <div>
                  <p className="font-medium text-gray-600">Disclaimer</p>
                  <p>Prices are subject to change. Contact your representative for current pricing and availability.</p>
                </div>
                <div className="text-right">
                  <p>&copy; {currentYear} {branding.companyName}</p>
                  <p>All rights reserved.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
